/**
 * Main-thread engine for camera-based part identification.
 *
 * The matching pipeline runs entirely in the browser (the Alpine backend stays
 * ML-free and only stores/ranks vectors), split across two threads:
 *
 * - Heavy inference — DINOv2 embedding and RMBG background removal — runs in a
 *   Web Worker ({@link ./identifyWorker}); each is a multi-second, effectively
 *   blocking WASM call, so keeping it off the UI thread is what stops the app
 *   from freezing. This module is the worker's client ({@link embedImages},
 *   {@link prepareAndEmbedFrames}).
 * - 3D rendering ({@link renderModelViews}) stays on the main thread because
 *   online-3d-viewer needs the DOM and WebGL; its rendered views are shipped to
 *   the worker as data-URLs to be embedded.
 */
import { t } from '@lingui/core/macro';

import { IDENTIFY_MODEL_ID } from './identifyModel';

// Re-export so existing importers (modal, index context) keep one import path.
export { IDENTIFY_MODEL_ID };

// Views rendered per model for the gallery. More views = better recall but
// slower indexing.
export const IDENTIFY_VIEW_COUNT = 6;

// Render resolution. DINOv2 resizes to 224 internally, so there's little point
// rendering larger.
const IMAGE_SIZE = 224;

// --- Inference worker client -----------------------------------------------

export interface PreparedQuery {
  /** One L2-normalised embedding per captured frame. */
  vectors: number[][];
  /** The last prepared (background-removed) frame, for operator preview. */
  preview: Blob | null;
}

let worker: Worker | null = null;
let nextRpcId = 1;
const pending = new Map<
  number,
  { resolve: (value: any) => void; reject: (error: any) => void }
>();

function getWorker(): Worker {
  if (!worker) {
    // Vite resolves and bundles the worker from this URL. `type: 'module'` lets
    // the worker use the same ESM + dynamic-import style as the rest of the app.
    worker = new Worker(new URL('./identifyWorker.ts', import.meta.url), {
      type: 'module'
    });
    worker.onmessage = (event: MessageEvent) => {
      const { id, ok, result, error } = event.data ?? {};
      const entry = pending.get(id);
      if (!entry) return;
      pending.delete(id);
      if (ok) entry.resolve(result);
      else entry.reject(new Error(error ?? 'identify worker error'));
    };
    worker.onerror = (event: ErrorEvent) => {
      // A worker-level crash rejects everything in flight so callers don't hang.
      const err = new Error(event.message || 'identify worker crashed');
      for (const entry of pending.values()) entry.reject(err);
      pending.clear();
    };
  }
  return worker;
}

function rpc<T>(op: string, payload: Record<string, unknown>): Promise<T> {
  const w = getWorker();
  const id = nextRpcId++;
  return new Promise<T>((resolve, reject) => {
    pending.set(id, { resolve, reject });
    w.postMessage({ id, op, ...payload });
  });
}

/**
 * Pre-load (download + initialise) the embedding and background-removal models
 * in the worker so the first match doesn't pay the cold start. Best-effort: the
 * caller fires this when the dialog opens and ignores failures (the next real
 * call will surface any genuine error). Idempotent — the worker caches both
 * pipelines, so repeat calls are cheap.
 */
export async function warmupModels(): Promise<void> {
  await rpc<boolean>('warmup', {});
}

/**
 * Embed images — model-render data-URLs, reference-image URLs, or Blobs — into
 * L2-normalised feature vectors. Runs in the worker.
 */
export async function embedImages(
  sources: Array<string | Blob>
): Promise<number[][]> {
  return rpc<number[][]>('embed', { sources });
}

/**
 * Background-remove + letterbox each captured camera frame and embed them.
 * Returns one vector per frame plus the last prepared frame for preview. Runs
 * in the worker, so the UI stays responsive during the heavy segmentation.
 */
export async function prepareAndEmbedFrames(
  frames: Blob[]
): Promise<PreparedQuery> {
  return rpc<PreparedQuery>('prepareAndEmbed', { frames });
}

// --- 3D rendering (main thread; needs the DOM + WebGL) ---------------------

interface RenderOptions {
  nViews?: number;
  size?: number;
  signal?: AbortSignal;
  /** Per-model timeout in ms (default {@link IDENTIFY_RENDER_TIMEOUT_MS}). */
  timeoutMs?: number;
}

// 30 s per model — occt-import-js (STEP) sometimes silently hangs inside its
// WASM parser without ever raising onLoadError, so we *have* to bound this
// from outside. A higher cap (originally 90 s) wastes minutes on parts that
// can't be rendered at all; 30 s is enough headroom for the slow-but-valid
// STEPs we've seen on this catalogue. Parts that legitimately need longer
// should ship an STL/GLB preview file.
export const IDENTIFY_RENDER_TIMEOUT_MS = 30_000;

// Skip parts whose 3D file exceeds this size via a HEAD pre-check — the
// in-browser STEP parser is heap-bound (iPad Safari OOMs around ~20 MB) and
// huge files are also the most likely to hit the timeout. Mark them as
// errored so the operator can attach a lighter preview file.
const IDENTIFY_MAX_RENDER_BYTES = 20 * 1024 * 1024;

/**
 * Probe a model URL with HEAD and return its size in bytes, or null if the
 * server doesn't expose Content-Length (CORS strips it for some hosts).
 * Network/CORS errors resolve to null so the caller can still attempt the
 * render — this is a pre-filter, not a hard gate.
 */
async function probeRenderSize(url: string): Promise<number | null> {
  try {
    const res = await fetch(url, { method: 'HEAD' });
    if (!res.ok) return null;
    const len = res.headers.get('Content-Length');
    if (!len) return null;
    const n = Number.parseInt(len, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/**
 * Wrapper around {@link renderModelViews} that short-circuits oversized
 * files with a descriptive error before launching the WASM parser. The
 * caller catches the throw and records it as a per-part indexing error.
 */
export async function renderModelViewsWithGuard(
  renderUrl: string,
  opts: RenderOptions = {}
): Promise<string[]> {
  const size = await probeRenderSize(renderUrl);
  if (size !== null && size > IDENTIFY_MAX_RENDER_BYTES) {
    const mb = (size / (1024 * 1024)).toFixed(1);
    const limitMb = (IDENTIFY_MAX_RENDER_BYTES / (1024 * 1024)).toFixed(0);
    throw new Error(
      t`3D file too large for in-browser rendering (${mb} MB, limit ${limitMb} MB). Attach an STL/GLB preview file.`
    );
  }
  return renderModelViews(renderUrl, opts);
}

/**
 * Render `nViews` images of a model around a turntable orbit, returned as
 * JPEG/PNG data-URLs. Uses an off-screen online-3d-viewer instance.
 *
 * Resolves `[]` if the URL isn't a browser-renderable mesh; rejects on load
 * failure or timeout.
 */
export async function renderModelViews(
  renderUrl: string,
  opts: RenderOptions = {}
): Promise<string[]> {
  const nViews = opts.nViews ?? IDENTIFY_VIEW_COUNT;
  const size = opts.size ?? IMAGE_SIZE;
  const OV: any = await import('online-3d-viewer');
  // Point the STEP / 3DM / Draco loaders at the locally-vendored copy so the
  // indexer doesn't depend on cdn.jsdelivr.net (see ovExternalLibs.ts).
  const { configureOvExternalLibs } = await import('../3d/ovExternalLibs');
  configureOvExternalLibs(OV);

  // WebGL needs the container in the DOM with a real size; park it off-screen
  // (not display:none, which would suspend rendering).
  const container = document.createElement('div');
  Object.assign(container.style, {
    position: 'fixed',
    left: '-10000px',
    top: '0',
    width: `${size}px`,
    height: `${size}px`
  });
  document.body.appendChild(container);

  let observer: MutationObserver | null = null;
  const teardown = (viewer?: any) => {
    observer?.disconnect();
    observer = null;
    try {
      viewer?.Destroy?.();
    } catch {
      // best-effort
    }
    container.remove();
  };

  const timeoutMs = opts.timeoutMs ?? IDENTIFY_RENDER_TIMEOUT_MS;

  return new Promise<string[]>((resolve, reject) => {
    let viewer: any;
    const timer = setTimeout(() => {
      teardown(viewer);
      // Include the URL so a logged failure points at the specific file —
      // huge or malformed STEP files are the usual culprit.
      const seconds = Math.round(timeoutMs / 1000);
      reject(
        new Error(
          `${t`3D rendering timed out after ${seconds} s.`} (${renderUrl})`
        )
      );
    }, timeoutMs);

    const onAbort = () => {
      clearTimeout(timer);
      teardown(viewer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    opts.signal?.addEventListener('abort', onAbort, { once: true });

    // online-3d-viewer reports load errors ("Failed to import model.", "The
    // model doesn't contain any meshes.", etc.) by writing them into a
    // transient overlay <div> rather than via a callback — so onModelLoaded
    // simply never fires for mesh-less STEP files and the indexer would
    // otherwise wait out the full timeoutMs on each one. Mirror the
    // MutationObserver trick from Model3DViewer to fail fast.
    // online-3d-viewer surfaces load failures by overwriting a progressDiv
    // (which during loading reads "Loading model..." / "Importing model..."
    // / "Visualizing model..."). When it switches to a failure string we
    // bail out instead of waiting out the timeout. ``textContent`` is used
    // instead of ``innerText`` because the off-screen container has no
    // layout, which can make ``innerText`` return empty in some browsers.
    const isProgressMessage = (s: string) =>
      /^(loading|importing|visualizing)\s+model/i.test(s);
    const isNoMeshes = (s: string) =>
      /no.*mesh|doesn.?t contain any meshes/i.test(s);
    observer = new MutationObserver(() => {
      const raw = (container.textContent ?? '').trim();
      if (!raw || isProgressMessage(raw)) return;
      // Once the canvas is in place the container has no readable text
      // content, so any non-progress text is treated as a failure overlay.
      clearTimeout(timer);
      opts.signal?.removeEventListener('abort', onAbort);
      teardown(viewer);
      const message = isNoMeshes(raw)
        ? t`The 3D file contains no meshes the in-browser viewer can render.`
        : t`The in-browser 3D viewer could not load the file.`;
      reject(new Error(`${message} (${renderUrl})`));
    });
    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true
    });

    try {
      viewer = new OV.EmbeddedViewer(container, {
        backgroundColor: new OV.RGBAColor(255, 255, 255, 255),
        defaultColor: new OV.RGBColor(120, 144, 168),
        edgeSettings: new OV.EdgeSettings(false, new OV.RGBColor(0, 0, 0), 1),
        environmentSettings: new OV.EnvironmentSettings([], false),
        onModelLoaded: () => {
          try {
            const v = viewer.GetViewer();
            const cam = v.GetCamera();
            const c = cam.center;
            const e = cam.eye;
            const fov = cam.fov;
            // Orbit radius = distance of the auto-fitted camera from the model.
            const radius =
              Math.hypot(e.x - c.x, e.y - c.y, e.z - c.z) || 1;

            const images: string[] = [];
            for (let i = 0; i < nViews; i++) {
              const az = (2 * Math.PI * i) / nViews;
              const el = ((i % 2 === 0 ? 20 : -10) * Math.PI) / 180;
              const eye = new OV.Coord3D(
                c.x + radius * Math.cos(el) * Math.sin(az),
                c.y + radius * Math.sin(el),
                c.z + radius * Math.cos(el) * Math.cos(az)
              );
              v.SetCamera(
                new OV.Camera(
                  eye,
                  new OV.Coord3D(c.x, c.y, c.z),
                  new OV.Coord3D(0, 1, 0),
                  fov
                )
              );
              images.push(v.GetImageAsDataUrl(size, size, false));
            }

            clearTimeout(timer);
            opts.signal?.removeEventListener('abort', onAbort);
            teardown(viewer);
            resolve(images);
          } catch (err) {
            clearTimeout(timer);
            teardown(viewer);
            reject(err);
          }
        }
      });
      viewer.LoadModelFromUrlList([renderUrl]);
    } catch (err) {
      clearTimeout(timer);
      teardown(viewer);
      reject(err);
    }
  });
}
